import { useCallback, useEffect, useState } from "react";
import { Copy, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorState, Loading } from "@/components/States";
import { ContextualNav } from "@/components/ContextualNav";

export default function ParentTutors() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/parent/family/members");
      setData(response.data);
      setError("");
    } catch (err) {
      setError(apiError(err, "No pudimos cargar los tutores."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data: created } = await api.post("/parent/family/invitations", { email });
      setInviteUrl(`${window.location.origin}/invitacion/${created.token}`);
      setEmail("");
      await load();
      toast.success("Invitacion creada. Comparte el enlace de forma privada.");
    } catch (err) {
      toast.error(apiError(err, "No pudimos crear la invitacion."));
    } finally {
      setBusy(false);
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Enlace copiado");
    } catch {
      toast.info("Selecciona y copia el enlace manualmente.");
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/parent/family/invitations/${id}`);
      await load();
    } catch (err) {
      toast.error(apiError(err, "No pudimos revocar la invitacion."));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Quitar el acceso de este tutor?")) return;
    try {
      await api.delete(`/parent/family/members/${id}`);
      await load();
    } catch (err) {
      toast.error(apiError(err, "No pudimos quitar al tutor."));
    }
  };

  if (error && !data) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Cargando tutores..." />;

  return (
    <div className="space-y-6" data-testid="parent-tutors-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Tutores de la familia</h1>
        <p className="mt-1 text-sm text-slate-500">Todos los tutores comparten el acceso académico de esta familia.</p>
      </div>

      {data.can_manage && (
        <form onSubmit={invite} className="estudia-card space-y-4 p-5">
          <div>
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><UserPlus className="h-5 w-5" /> Invitar tutor</h2>
            <p className="mt-1 text-sm text-slate-500">El enlace caduca en siete días y sólo puede utilizarse una vez.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="tutor-email">Correo del tutor</Label>
              <Input id="tutor-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} className="estudia-touch mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} className="estudia-touch rounded-xl bg-blue-600 font-bold hover:bg-blue-700">Crear invitacion</Button>
          </div>
          {inviteUrl && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900">Copia ahora este enlace; el token no volverá a mostrarse.</p>
              <div className="mt-2 flex gap-2">
                <Input readOnly value={inviteUrl} className="min-w-0 bg-white" />
                <Button type="button" variant="outline" size="icon" onClick={copyInvite} aria-label="Copiar enlace"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </form>
      )}

      <section className="estudia-card p-5">
        <h2 className="font-display text-lg font-bold text-slate-900">Con acceso</h2>
        <div className="mt-4 divide-y divide-slate-100">
          {data.members.map((member) => (
            <div key={member.id} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-800">{member.name}{member.is_current ? " (tú)" : ""}</p>
                <p className="truncate text-sm text-slate-500">{member.email} · {member.member_role === "owner" ? "Propietario" : "Tutor"}</p>
              </div>
              {data.can_manage && member.member_role !== "owner" && (
                <Button variant="ghost" size="icon" onClick={() => remove(member.id)} aria-label={`Quitar a ${member.name}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              )}
            </div>
          ))}
        </div>
      </section>

      {data.can_manage && data.invitations.length > 0 && (
        <section className="estudia-card p-5">
          <h2 className="font-display text-lg font-bold text-slate-900">Invitaciones pendientes</h2>
          <div className="mt-4 divide-y divide-slate-100">
            {data.invitations.map((invitation) => (
              <div key={invitation.id} className="flex items-center justify-between gap-4 py-3">
                <div><p className="font-semibold text-slate-800">{invitation.email}</p><p className="text-xs text-slate-500">{invitation.expired ? "Caducada" : `Caduca ${new Date(invitation.expires_at).toLocaleDateString()}`}</p></div>
                <Button variant="ghost" size="icon" onClick={() => revoke(invitation.id)} aria-label={`Revocar invitacion de ${invitation.email}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <ContextualNav
        title="Gestiona la familia desde un solo lugar"
        description="Los tutores comparten el seguimiento; los perfiles de estudiante conservan sus propios accesos."
        items={[
          { to: "/p/estudiantes", icon: "students", label: "Ver perfiles", description: "Usuarios, PIN y modo de juego." },
          { to: "/p", icon: "dashboard", label: "Volver al panel", description: "Revisa la actividad familiar." },
        ]}
      />
    </div>
  );
}
